import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import styled from 'styled-components';
import { useParams } from 'react-router';
import { Workstation } from '../../store/types';
import { useTypedDispatch, useTypedSelector } from '../../hooks';
import {
  updateWorkstation,
  workstationsSelector,
  getWorkstation,
  getWorkstationLabels,
  assignWorkstationLabels,
  unassignWorkstationLabels,
  sessionsSelector,
  getWorkstations,
} from '../../store/reducers';
import { AddCircleOutlined, Done, NavigateNext } from '@mui/icons-material';
import { SessionsList } from '../../components/Pages/workstations/SessionsList';

const StyledLink = styled(Link)`
  color: black;
  text-decoration: none;
  position: relative;
  font-weight: 400;

  &:hover {
    border-bottom: 3px solid #c62828;
    color: black;
  }
`;

const WorkstationUpdate: React.FC = function () {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const dispatch = useTypedDispatch();

  const { id: workstationId } = useParams();

  const SessionsReference = useRef<HTMLElement>(null);

  const {
    isLoading: isLoadingWorkstation,
    currentWorkstation,
    workstations,
    error: errorWorkstation,
    assignedLabels,
    labels,
  } = useTypedSelector(workstationsSelector);
  const { isLoading: isLoadingSession, sessions, error: errorSession } = useTypedSelector(sessionsSelector);

  const [description, setDescription] = useState<string>(currentWorkstation?.description || '');
  const [name, setName] = useState<string>(currentWorkstation?.name);

  const [newLabel, setNewLabel] = useState<string>('');
  const [isLabelsInputOpen, setLabelsInputOpen] = React.useState(false);

  const newLabelInputHandler = useCallback(
    (e) => {
      setNewLabel(e.target.value);
    },
    [setNewLabel],
  );

  useEffect(() => {
    if (!isLoadingWorkstation) {
      dispatch(getWorkstation(workstationId || ''));
      dispatch(getWorkstationLabels());
    }
  }, []);

  useEffect(() => {
    if (errorWorkstation) {
      enqueueSnackbar(errorWorkstation, { variant: 'error' });
    }
    if (errorSession) {
      enqueueSnackbar(errorSession, { variant: 'error' });
    }
  }, [errorSession, errorWorkstation]);

  const workstationSubmitHandler = useCallback(
    (e) => {
      e.preventDefault();

      dispatch(
        updateWorkstation({
          id: Number(workstationId),
          name: name,
          description: description,
          labels: assignedLabels,
        }),
      ).then((data: { error: any }) => {
        if (!data.error) {
          enqueueSnackbar('Workstation has been successfully updated!', {
            variant: 'success',
          });
          dispatch(getWorkstations());
        }
      });

      navigate('/workstations');
    },
    [name, description, assignedLabels, workstationId],
  );

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '15px',
      }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '15px',
        }}
      >
        <Typography variant="h6" align="left">
          Update workstation {workstationId}
        </Typography>
      </Box>

      <Divider />

      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNext fontSize="small" />}
        sx={{
          padding: '15px',
        }}
      >
        <StyledLink to="/workstations">Workstations</StyledLink>
        <Typography
          color="black"
          style={{
            fontWeight: '700',
          }}
        >
          {workstationId}
        </Typography>
      </Breadcrumbs>

      <Paper>
        <Box
          style={{
            padding: '15px',
          }}
        >
          <FormControl required variant="outlined" fullWidth style={{ marginTop: '15px' }}>
            <TextField
              disabled
              value={workstationId}
              id="workstation-id"
              label={'Workstation ID *'}
              aria-describedby="workstation-id"
            />
          </FormControl>

          <FormControl required variant="outlined" fullWidth style={{ marginTop: '15px' }}>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              id="workstation-name"
              label={'Workstation Name'}
              aria-describedby="workstation-name"
            />
          </FormControl>

          <TextField
            variant="outlined"
            multiline
            fullWidth
            maxRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            id="workstation-description"
            label={'Description'}
            aria-describedby="role-description-label"
            style={{ marginTop: '15px' }}
          />

          <FormControl
            variant="outlined"
            fullWidth
            style={{ marginTop: '15px', marginRight: '15px', display: 'flex', flexDirection: 'row', width: '100%' }}
          >
            <InputLabel id="labels-label">Labels</InputLabel>
            <Select
              fullWidth
              multiple
              labelId="labels-label"
              label={'Labels'}
              id="labels"
              startAdornment={
                isLabelsInputOpen ? (
                  <TextField
                    autoFocus
                    onChange={newLabelInputHandler}
                    variant="standard"
                    style={{ margin: '5px 15px 5px 0' }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        if (!assignedLabels?.some((label) => label === newLabel))
                          dispatch(assignWorkstationLabels([...assignedLabels, newLabel]));
                        setLabelsInputOpen(false);
                      }
                    }}
                  />
                ) : (
                  <IconButton onClick={() => setLabelsInputOpen(true)}>
                    <AddCircleOutlined />
                  </IconButton>
                )
              }
              value={assignedLabels}
              onChange={(e: SelectChangeEvent<string[]>) => dispatch(assignWorkstationLabels(e.target.value))}
              renderValue={(selectedList) =>
                isLoadingWorkstation ? (
                  <CircularProgress size={32} style={{ marginLeft: '15px' }} />
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedList?.map((selected) => (
                      <>
                        <Chip
                          key={selected}
                          label={selected}
                          variant={'outlined'}
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                          onDelete={() => dispatch(unassignWorkstationLabels(selected))}
                        />
                      </>
                    ))}
                  </Box>
                )
              }
            >
              {isLoadingWorkstation ? (
                <CircularProgress style={{ marginLeft: '15px' }} />
              ) : (
                labels.length &&
                labels?.map((lbl) => (
                  <MenuItem key={lbl} value={lbl}>
                    {assignedLabels?.some((label) => label === lbl) ? <Done /> : <Box width={'24px'} />}
                    {lbl}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>

        {isLoadingSession ? (
          <Skeleton
            width={SessionsReference?.current?.offsetWidth || '90%'}
            height={SessionsReference?.current?.offsetHeight || '90%'}
          />
        ) : (
          <Box
            ref={SessionsReference}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'start',
              width: '100%',
              flexGrow: 1,
              padding: '15px',
            }}
          >
            <Typography variant={'h6'}>Sessions</Typography>
            <SessionsList sessions={sessions} />
          </Box>
        )}

        <Divider />

        <Box display={'flex'} justifyContent={'end'} padding={'15px'}>
          <Button variant={'outlined'} style={{ marginRight: '15px' }} onClick={() => navigate('/workstations')}>
            Cancel
          </Button>
          <Button variant={'contained'} onClick={workstationSubmitHandler}>
            Update
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default WorkstationUpdate;
